import {
  ExtraProduct,
  TProductTypeLitter,
  Product,
} from "libs/api/customer/src/lib/types/products";
import { FreshExtraProduct } from "@api-clients/subscriptions";

import { SubscriptionItemType } from "@/primary-models";
import dayjs from "dayjs";

const PRODUCT_NAMES: string[] = [
  "CLUCK",
  "GOBBLE",
  "QUACK",
  "MOOO",
  "OINK",
  "BAAA",
  "SWISH",
  "SPLASH",
  "SMOOTH_CLUCK",
  "SMOOTH_PECK",
  "NIBBLES_CHICKEN",
  "NIBBLES_SALMON",
  "NIBBLES_CHICKEN_POUCH",
  "NIBBLES_SALMON_POUCH",
  "NIBBLES_TUNA_POUCH",
  "PLANET_SCOOP",
  "HEALTH_SCOOP",
  "SPRINKLES",
];

export * from "./fresh.types";

export type TProductName = typeof PRODUCT_NAMES[number];

export type TParentProductName = "FRESH" | "HEALTH_SCOOP" | "PLANET_SCOOP";

export enum EProduct {
  CLUCK,
  GOBBLE,
  QUACK,
  MOOO,
  OINK,
  BAAA,
  SWISH,
  SPLASH,
  SMOOTH_CLUCK,
  SMOOTH_PECK,
  NIBBLES_CHICKEN,
  NIBBLES_SALMON,
  NIBBLES_CHICKEN_POUCH,
  NIBBLES_SALMON_POUCH,
  NIBBLES_TUNA_POUCH,
  PLANET_SCOOP,
  HEALTH_SCOOP,
  SPRINKLES,
}

export enum EProductCategory {
  FRESH = "FRESH",
  SCOOP = "SCOOP",
  UNKNOWN = "UNKNOWN",
  EXTRAS = "EXTRAS",
}

/**
 * Filename for each product should be consistent across all folders e.g. cluck.webp
 */
interface IProductFileNames {
  image: string;
}

interface IProductIngredients {
  core: string[];
}

export interface IProduct {
  key: string;
  title: string;
  ingredients: IProductIngredients;
  productCategory: EProductCategory;
  fileNames: IProductFileNames;
  imageClassName?: string;
}

const SHREDDED_CLUCK: IProduct = {
  key: "SHREDDED_CLUCK",
  title: "C Shredded",
  ingredients: { core: [ "chicken", "salmon" ] },
  productCategory: EProductCategory.FRESH,
  fileNames: { image: "shredded-cluck.webp" },
};

const SHREDDED_GOBBLE: IProduct = {
  key: "SHREDDED_GOBBLE",
  title: "G Shredded",
  ingredients: { core: [ "turkey", "chicken", "salmon" ] },
  productCategory: EProductCategory.FRESH,
  fileNames: { image: "shredded-gobble.webp" },
};

export const SHREDDED_PRODUCTS: IProduct[] = [ SHREDDED_CLUCK, SHREDDED_GOBBLE ];

export type TProductMap = Record<EProduct, IProduct>;

export const PRODUCT_NAME_TO_ENUM_MAP: Record<TProductName, EProduct> = {
  CLUCK: EProduct.CLUCK,
  GOBBLE: EProduct.GOBBLE,
  QUACK: EProduct.QUACK,
  MOOO: EProduct.MOOO,
  OINK: EProduct.OINK,
  BAAA: EProduct.BAAA,
  SWISH: EProduct.SWISH,
  SPLASH: EProduct.SPLASH,
  SMOOTH_CLUCK: EProduct.SMOOTH_CLUCK,
  SMOOTH_PECK: EProduct.SMOOTH_PECK,
  NIBBLES_CHICKEN: EProduct.NIBBLES_CHICKEN,
  NIBBLES_SALMON: EProduct.NIBBLES_SALMON,
  NIBBLES_CHICKEN_POUCH: EProduct.NIBBLES_CHICKEN_POUCH,
  NIBBLES_SALMON_POUCH: EProduct.NIBBLES_SALMON_POUCH,
  NIBBLES_TUNA_POUCH: EProduct.NIBBLES_TUNA_POUCH,
  SCOOP_PLANET: EProduct.PLANET_SCOOP,
  SCOOP_HEALTH: EProduct.HEALTH_SCOOP,
  SPRINKLES: EProduct.SPRINKLES,
};

export const PRODUCT_MAP: TProductMap = {
  [EProduct.CLUCK]: {
    key: "CLUCK",
    title: "Cluck",
    ingredients: {
      core: [ "chicken" ],
    },
    productCategory: EProductCategory.FRESH,
    fileNames: {
      image: "cluck.webp",
    },
  },
  [EProduct.GOBBLE]: {
    key: "GOBBLE",
    title: "Gobble",
    ingredients: {
      core: [ "turkey" ],
    },
    productCategory: EProductCategory.FRESH,
    fileNames: {
      image: "gobble_2026.webp",
    },
    imageClassName: "scale-125",
  },
  [EProduct.QUACK]: {
    key: "QUACK",
    title: "Quack",
    ingredients: {
      core: [ "duck" ],
    },
    productCategory: EProductCategory.FRESH,
    fileNames: {
      image: "quack.webp",
    },
  },
  [EProduct.MOOO]: {
    key: "MOOO",
    title: "Mooo",
    ingredients: {
      core: [ "beef" ],
    },
    productCategory: EProductCategory.FRESH,
    fileNames: {
      image: "mooo_2026.webp",
    },
    imageClassName: "scale-125",
  },
  [EProduct.OINK]: {
    key: "OINK",
    title: "Oink",
    ingredients: {
      core: [ "pork" ],
    },
    productCategory: EProductCategory.FRESH,
    fileNames: {
      image: "oink.webp",
    },
  },
  [EProduct.BAAA]: {
    key: "BAAA",
    title: "Baaa",
    ingredients: {
      core: [ "lamb" ],
    },
    productCategory: EProductCategory.FRESH,
    fileNames: {
      image: "baaa.webp",
    },
  },
  [EProduct.SWISH]: {
    key: "SWISH",
    title: "Swish",
    ingredients: {
      core: [ "tuna", "salmon", "chicken" ],
    },
    productCategory: EProductCategory.FRESH,
    fileNames: {
      image: "swish_2026.webp",
    },
    imageClassName: "scale-125",
  },
  [EProduct.SPLASH]: {
    key: "SPLASH",
    title: "Splash",
    ingredients: {
      core: [ "fish", "turkey" ],
    },
    productCategory: EProductCategory.FRESH,
    fileNames: {
      image: "splash.webp",
    },
  },
  [EProduct.SMOOTH_CLUCK]: {
    key: "SMOOTH_CLUCK",
    title: "C Smooth",
    ingredients: {
      core: [ "chicken" ],
    },
    productCategory: EProductCategory.FRESH,
    fileNames: {
      image: "smooth-cluck.webp",
    },
    imageClassName: "scale-125",
  },
  [EProduct.SMOOTH_PECK]: {
    key: "SMOOTH_PECK",
    title: "P Smooth",
    ingredients: {
      core: [ "chicken", "turkey", "duck" ],
    },
    productCategory: EProductCategory.FRESH,
    fileNames: {
      image: "smooth-peck.webp",
    },
    imageClassName: "scale-125",
  },
  [EProduct.NIBBLES_CHICKEN]: {
    key: "NIBBLES_CHICKEN",
    title: "Chicken nibbles 50g",
    ingredients: {
      core: [ "chicken" ],
    },
    productCategory: EProductCategory.EXTRAS,
    fileNames: {
      image: "nibbles-chicken.webp",
    },
  },
  [EProduct.NIBBLES_SALMON]: {
    key: "NIBBLES_SALMON",
    title: "Salmon nibbles 50g",
    ingredients: {
      core: [ "fish" ],
    },
    productCategory: EProductCategory.EXTRAS,
    fileNames: {
      image: "nibbles-salmon.webp",
    },
  },
  [EProduct.NIBBLES_CHICKEN_POUCH]: {
    key: "NIBBLES_CHICKEN_POUCH",
    title: "Chicken nibbles 15g",
    ingredients: {
      core: [ "chicken" ],
    },
    productCategory: EProductCategory.EXTRAS,
    fileNames: {
      image: "nibbles-chicken-pouch.webp",
    },
  },
  [EProduct.NIBBLES_SALMON_POUCH]: {
    key: "NIBBLES_SALMON_POUCH",
    title: "Salmon nibbles 15g",
    ingredients: {
      core: [ "fish" ],
    },
    productCategory: EProductCategory.EXTRAS,
    fileNames: {
      image: "nibbles-salmon-pouch.webp",
    },
  },
  [EProduct.NIBBLES_TUNA_POUCH]: {
    key: "NIBBLES_TUNA_POUCH",
    title: "Tuna nibbles 15g",
    ingredients: {
      core: [ "fish" ],
    },
    productCategory: EProductCategory.EXTRAS,
    fileNames: {
      image: "nibbles-tuna-pouch.webp",
    },
  },
  [EProduct.SPRINKLES]: {
    key: "SPRINKLES",
    title: "Chicken sprinkles",
    ingredients: {
      core: [ "chicken" ],
    },
    productCategory: EProductCategory.EXTRAS,
    fileNames: {
      image: "sprinkles-chicken.webp",
    },
  },
  [EProduct.HEALTH_SCOOP]: {
    key: "HEALTH_SCOOP",
    title: "Health",
    ingredients: {
      core: [],
    },
    productCategory: EProductCategory.SCOOP,
    fileNames: {
      image: "scoop-health.webp",
    },
  },
  [EProduct.PLANET_SCOOP]: {
    key: "PLANET_SCOOP",
    title: "Planet",
    ingredients: {
      core: [],
    },
    productCategory: EProductCategory.SCOOP,
    fileNames: {
      image: "scoop-planet.webp",
    },
  },
};

export const EXTRA_PRODUCT: Partial<Record<ExtraProduct, string>> = {
  HEALTH_SCOOP_LITTER: "Scoop Health",
  PLANET_SCOOP_LITTER: "Scoop Planet",
};

export const EXTRA_FRESH_PRODUCT: Record<FreshExtraProduct, string> = {
  NIBBLES_CHICKEN: "Chicken Nibbles 50g",
  NIBBLES_SALMON: "Salmon Nibbles 50g",
  SPRINKLES: "Sprinkles",
  NIBBLES_CHICKEN_POUCH: "Chicken Nibbles 15g",
  NIBBLES_SALMON_POUCH: "Salmon Nibbles 15g",
  NIBBLES_TUNA_POUCH: "Tuna Nibbles 15g",
};

export const VALID_SCOOP_DELIVERY_INTERVALS: number[] = Array.from({ length: 16 }, (_, value) => (value + 1));

const MAP_PRODUCT_NAME_VARIANTS_TO_DEFAULT: Record<Product, TProductName> = {
  NIBBLES_SALMON: "NIBBLES_SALMON",
  NIBBLES_CHICKEN: "NIBBLES_CHICKEN",
  NIBBLES_SALMON_POUCH: "NIBBLES_SALMON_POUCH",
  NIBBLES_CHICKEN_POUCH: "NIBBLES_CHICKEN_POUCH",
  NIBBLES_TUNA_POUCH: "NIBBLES_TUNA_POUCH",
  SPRINKLES: "SPRINKLES",
  CLUCK: "CLUCK",
  GOBBLE: "GOBBLE",
  QUACK: "QUACK",
  MOOO: "MOOO",
  OINK: "OINK",
  BAAA: "BAAA",
  SWISH: "SWISH",
  SPLASH: "SPLASH",
  SHREDDED_CLUCK: "SHREDDED_CLUCK",
  SHREDDED_GOBBLE: "SHREDDED_GOBBLE",
  SMOOTH_CLUCK: "SMOOTH_CLUCK",
  SMOOTH_PECK: "SMOOTH_PECK",
  BUBBLE_AND_QUACK: "BUBBLE_AND_QUACK",
  TOFU_SCOOP: "TOFU_SCOOP",
  PLANET_SCOOP: "PLANET_SCOOP",
  HEALTH_SCOOP: "HEALTH_SCOOP",
  POUCH_CHICKEN: "CLUCK",
  POUCH_TURKEY: "GOBBLE",
  POUCH_BEEF: "MOOO",
  CLUCK_2404: "CLUCK",
  GOBBLE_2404: "GOBBLE",
  QUACK_2404: "QUACK",
  OINK_2404: "OINK",
  SPLASH_2404: "SPLASH",
  BAAA_2404: "BAAA",
  MOOO_2404: "MOOO",
};

export const mapProductNameVariantsToDefault = (productNameVariant: Product): TProductName => {
  return MAP_PRODUCT_NAME_VARIANTS_TO_DEFAULT[productNameVariant];
};

export function isValidProductName(name: string): name is TProductName {
  return PRODUCT_NAMES.includes(name);
}

export const getProductFromName = (name: TProductName): IProduct => {
  const productEnum = PRODUCT_NAME_TO_ENUM_MAP[name];

  return PRODUCT_MAP[productEnum];
};

const IMAGE_KEY_TO_FOLDER_MAP = {
  MEAT_CONTENT: "meat-content",
  PACKAGING: "packaging",
} as const;

type TImageKey = keyof typeof IMAGE_KEY_TO_FOLDER_MAP;

interface IProductImage {
  src: string;
  width: number;
  height: number;
}

export const getProductImage = (fileName: string, key: TImageKey): IProductImage => ({
  src: `/images/features/product-overview/${IMAGE_KEY_TO_FOLDER_MAP[key]}/${fileName}`,
  width: 800,
  height: 800,
});

export const transformLitterProductTypeToProductName = (type: TProductTypeLitter): TProductName => {
  const map: Record<TProductTypeLitter, TProductName> = {
    HEALTH_SCOOP_LITTER: "HEALTH_SCOOP",
    PLANET_SCOOP_LITTER: "PLANET_SCOOP",
  };

  return map[type];
};

export const transformProductNameToLitterProductType = (type: TProductName): TProductTypeLitter => {
  const map: Record<TProductName, TProductTypeLitter> = {
    HEALTH_SCOOP: "HEALTH_SCOOP_LITTER",
    PLANET_SCOOP: "PLANET_SCOOP_LITTER",
  };

  return map[type];
};

export const DEFAULT_DELIVERY_FEE = 2.99;

export const MAX_FRESH_RECIPE_QUANTITY = 42;

export const SUBSCRIPTION_ITEM_TYPE_TO_PRETTY_PRODUCT_TITLE_MAP: Record<SubscriptionItemType, string> = {
  [SubscriptionItemType.CLUCK]: "Cluck",
  [SubscriptionItemType.GOBBLE]: "Gobble",
  [SubscriptionItemType.SHREDDED_CLUCK]: "Shredded Cluck",
  [SubscriptionItemType.SHREDDED_GOBBLE]: "Shredded Gobble",
  [SubscriptionItemType.QUACK]: "Quack",
  [SubscriptionItemType.MOOO]: "Mooo",
  [SubscriptionItemType.OINK]: "Oink",
  [SubscriptionItemType.SPLASH]: "Splash",
  [SubscriptionItemType.BAAA]: "Baaa",
  [SubscriptionItemType.SWISH]: "Swish",
  [SubscriptionItemType.SMOOTH_PECK]: "Smooth Peck",
  [SubscriptionItemType.SMOOTH_CLUCK]: "Smooth Cluck",
  [SubscriptionItemType.NIBBLES_CHICKEN]: "Chicken Nibbles 50g",
  [SubscriptionItemType.NIBBLES_SALMON]: "Salmon Nibbles 50g",
  [SubscriptionItemType.NIBBLES_CHICKEN_POUCH]: "Chicken Nibbles 15g",
  [SubscriptionItemType.NIBBLES_SALMON_POUCH]: "Salmon Nibbles 15g",
  [SubscriptionItemType.NIBBLES_TUNA_POUCH]: "Tuna Nibbles 15g",
  [SubscriptionItemType.SPRINKLES]: "Sprinkles",
  [SubscriptionItemType.SCOOP_HEALTH]: "Scoop Health",
  [SubscriptionItemType.SCOOP_PLANET]: "Scoop Planet",
  [SubscriptionItemType.TOTE_BAG]: "Tote bag",
};

const SCOOP_ITEMS: Record<SubscriptionItemType, boolean> = {
  CLUCK: false,
  GOBBLE: false,
  QUACK: false,
  OINK: false,
  SPLASH: false,
  BAAA: false,
  MOOO: false,
  SHREDDED_CLUCK: false,
  SHREDDED_GOBBLE: false,
  SWISH: false,
  SMOOTH_PECK: false,
  SMOOTH_CLUCK: false,
  SPRINKLES: false,
  NIBBLES_CHICKEN: false,
  NIBBLES_SALMON: false,
  NIBBLES_CHICKEN_POUCH: false,
  NIBBLES_SALMON_POUCH: false,
  NIBBLES_TUNA_POUCH: false,
  SCOOP_HEALTH: true,
  SCOOP_PLANET: true,
  TOTE_BAG: false,
};

export const isScoopItem = (title: SubscriptionItemType): boolean => SCOOP_ITEMS[title];

const NIBBLES_ITEMS: Record<SubscriptionItemType, boolean> = {
  CLUCK: false,
  GOBBLE: false,
  QUACK: false,
  OINK: false,
  SPLASH: false,
  BAAA: false,
  MOOO: false,
  SHREDDED_CLUCK: false,
  SHREDDED_GOBBLE: false,
  SWISH: false,
  SMOOTH_PECK: false,
  SMOOTH_CLUCK: false,
  SPRINKLES: false,
  NIBBLES_CHICKEN: true,
  NIBBLES_SALMON: true,
  NIBBLES_CHICKEN_POUCH: true,
  NIBBLES_SALMON_POUCH: true,
  NIBBLES_TUNA_POUCH: true,
  SCOOP_HEALTH: false,
  SCOOP_PLANET: false,
  TOTE_BAG: false,
};

export const isNibblesItem = (title: SubscriptionItemType): boolean => NIBBLES_ITEMS[title];

// Sprinkels v2 go-live date.
// Orders charged before this date shipped the legacy sprinkles jar.
export const SPRINKLES_V2_LAUNCH_DATE = dayjs("2026-07-20");

const LEGACY_SPRINKLES_PRODUCT: IProduct = {
  ...PRODUCT_MAP[EProduct.SPRINKLES],
  fileNames: {
    image: "deprecated_sprinkles.png",
  },
};

export function getReceiptSprinklesProduct(chargeDate: string | null | undefined): IProduct {
  if (!chargeDate || dayjs(chargeDate).isBefore(SPRINKLES_V2_LAUNCH_DATE)) {
    return LEGACY_SPRINKLES_PRODUCT;
  }

  return PRODUCT_MAP[EProduct.SPRINKLES];
}
